import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb,
  faFireFlameCurved,
  faBolt,
  faMoneyBillWave,
  faArrowRight,
  faArrowLeft,
  faCheck,
  faSpinner,
  faShieldHalved,
  faPhone,
  faGift,
  faClock,
  faUsers,
  faChartLine,
  faExchangeAlt,
  faPlusCircle,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { logo } from '../assets';
import { persistUtmFromUrl, submitFormRecall } from '../utils/form-submissions';
import PrivacyConsents, { emptyPrivacyConsents } from '../components/PrivacyConsents';

const BRAND = 'serviziobolletta.it';

interface QuizAnswers {
  tipo_fornitura: string;
  spesa_mensile: string;
  situazione: string;
}

interface ContactData {
  nome: string;
  cognome: string;
  telefono: string;
  privacy1: boolean;
  privacy2: boolean;
  privacy3: boolean;
}

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

const QuizLanding: React.FC = () => {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState<QuizAnswers>({
    tipo_fornitura: '',
    spesa_mensile: '',
    situazione: '',
  });
  const [contact, setContact] = useState<ContactData>({
    nome: '',
    cognome: '',
    telefono: '',
    ...emptyPrivacyConsents(),
  });
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  useEffect(() => {
    persistUtmFromUrl();
  }, []);

  const selectAnswer = useCallback((key: keyof QuizAnswers, value: string) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
    setTimeout(() => setStep(s => s + 1), 300);
  }, []);

  const validateContact = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!contact.nome.trim()) newErrors.nome = 'Obbligatorio';
    if (!contact.cognome.trim()) newErrors.cognome = 'Obbligatorio';

    const tel = contact.telefono.replace(/[\s\-().+]/g, '');
    if (!tel) {
      newErrors.telefono = 'Obbligatorio';
    } else if (!/^\d{9,13}$/.test(tel)) {
      newErrors.telefono = 'Inserisci un numero valido (min. 9 cifre)';
    }

    if (!contact.privacy1) {
      newErrors.privacy1 = 'Devi accettare l\'informativa privacy e i termini e condizioni';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateContact()) return;

    setFormStatus('loading');

    const urlParams = new URLSearchParams(window.location.search);
    const idsms = urlParams.get('idsms');

    try {
      await submitFormRecall(
        {
          nome: contact.nome,
          cognome: contact.cognome,
          telefono: contact.telefono,
          privacy1: contact.privacy1,
          privacy2: contact.privacy2,
          privacy3: contact.privacy3,
        },
        {
          brand: BRAND,
          idsms,
          origine: 'quiz_offerta',
          extra: {
            tipo_fornitura: answers.tipo_fornitura,
            spesa_mensile: answers.spesa_mensile,
            situazione: answers.situazione,
          },
        },
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'generate_lead',
        lead_source: 'quiz_landing',
        lead_service: answers.tipo_fornitura,
        lead_spesa: answers.spesa_mensile,
      });

      setFormStatus('success');
      setStep(4);
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 4000);
    }
  };

  const progress = Math.min((Math.max(step, 0) / 4) * 100, 100);

  const quizQuestions = [
    {
      title: 'Che tipo di fornitura ti interessa?',
      subtitle: 'Seleziona il servizio per cui vuoi confrontare le offerte',
      key: 'tipo_fornitura' as const,
      options: [
        { value: 'luce', label: 'Solo Luce', icon: faLightbulb, color: 'from-yellow-400 to-amber-500' },
        { value: 'gas', label: 'Solo Gas', icon: faFireFlameCurved, color: 'from-orange-400 to-red-500' },
        { value: 'luce_gas', label: 'Luce e Gas', icon: faBolt, color: 'from-primary to-blue-600' },
      ],
    },
    {
      title: 'Quanto spendi al mese in bolletta?',
      subtitle: 'Indica la tua spesa media mensile attuale',
      key: 'spesa_mensile' as const,
      options: [
        { value: 'meno_50', label: 'Meno di 50€', icon: faMoneyBillWave, color: 'from-green-400 to-emerald-500' },
        { value: '50_100', label: '50€ – 100€', icon: faMoneyBillWave, color: 'from-teal-400 to-cyan-500' },
        { value: '100_150', label: '100€ – 150€', icon: faMoneyBillWave, color: 'from-blue-400 to-indigo-500' },
        { value: 'oltre_150', label: 'Oltre 150€', icon: faMoneyBillWave, color: 'from-purple-400 to-violet-500' },
      ],
    },
    {
      title: 'Qual è la tua situazione attuale?',
      subtitle: 'Così possiamo fornirti un confronto più mirato',
      key: 'situazione' as const,
      options: [
        { value: 'cambiare', label: 'Voglio cambiare fornitore', icon: faExchangeAlt, color: 'from-rose-400 to-pink-500' },
        { value: 'confrontare', label: 'Voglio solo confrontare', icon: faChartLine, color: 'from-sky-400 to-blue-500' },
        { value: 'attivare', label: 'Devo attivare una nuova fornitura', icon: faPlusCircle, color: 'from-emerald-400 to-green-500' },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Confronta Offerte Luce e Gas — Risparmia in Bolletta | Servizio Bolletta</title>
        <meta name="description" content="Rispondi a 3 semplici domande e ricevi una consulenza gratuita per risparmiare sulle bollette luce e gas. Servizio indipendente, gratuito e senza obbligo." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary/5 flex flex-col">
        {/* Header minimo */}
        <header className="py-4 px-4 bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src={logo} alt="Servizio Bolletta" className="h-8" />
            </Link>
            <a
              href="tel:+390240055045"
              className="flex items-center gap-2 text-sm font-medium text-primary hover:text-secondary transition-colors"
            >
              <FontAwesomeIcon icon={faPhone} />
              <span className="hidden sm:inline">02 4005 5045</span>
            </a>
          </div>
        </header>

        {/* Progress bar */}
        {step < 4 && (
          <div className="w-full bg-gray-100">
            <motion.div
              className="h-1.5 bg-gradient-to-r from-primary to-secondary rounded-r-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        )}

        <main className="flex-1 flex flex-col items-center justify-center px-4 py-8 md:py-12">
          <AnimatePresence mode="wait">
            {/* Hero / Intro */}
            {step === -1 && (
              <motion.div
                key="hero"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.4 }}
                className="max-w-3xl w-full text-center"
              >
                <div className="mb-8">
                  <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 text-sm font-medium px-4 py-2 rounded-full mb-6 border border-green-200">
                    <FontAwesomeIcon icon={faGift} />
                    Consulenza 100% gratuita
                  </div>
                  <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
                    Stai pagando <span className="text-primary">troppo</span> in bolletta?
                  </h1>
                  <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto">
                    Rispondi a <strong className="text-gray-700">3 semplici domande</strong> e scopri 
                    quanto puoi risparmiare con un'offerta più adatta a te
                  </p>
                </div>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => setStep(0)}
                  type="button"
                  className="inline-flex items-center gap-3 bg-primary hover:bg-primary/90 text-white font-semibold text-lg px-10 py-4 rounded-xl shadow-lg shadow-primary/25 transition-colors"
                >
                  Inizia ora — è gratis
                  <FontAwesomeIcon icon={faArrowRight} />
                </motion.button>

                <div className="mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto">
                  {[
                    { icon: faClock, text: '2 minuti' },
                    { icon: faShieldHalved, text: 'Senza obbligo' },
                    { icon: faUsers, text: '15.000+ consulenze' },
                  ].map(b => (
                    <div key={b.text} className="flex flex-col items-center gap-1.5 text-gray-400">
                      <FontAwesomeIcon icon={b.icon} className="text-primary/60" />
                      <span className="text-xs font-medium">{b.text}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Quiz steps (0, 1, 2) */}
            {step >= 0 && step <= 2 ? (
              <motion.div
                key={`quiz-${step}`}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.35 }}
                className="max-w-2xl w-full"
              >
                <div className="text-center mb-8">
                  <p className="text-sm font-medium text-primary mb-2">
                    Domanda {step + 1} di 3
                  </p>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    {quizQuestions[step].title}
                  </h2>
                  <p className="text-gray-500">
                    {quizQuestions[step].subtitle}
                  </p>
                </div>

                <div className={`grid gap-4 ${quizQuestions[step].options.length === 4 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-3'}`}>
                  {quizQuestions[step].options.map((opt) => (
                    <motion.button
                      key={opt.value}
                      whileHover={{ scale: 1.03, y: -2 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => selectAnswer(quizQuestions[step].key, opt.value)}
                      className={`relative p-6 rounded-xl border-2 transition-all text-left group ${
                        answers[quizQuestions[step].key] === opt.value
                          ? 'border-primary bg-primary/5 shadow-md'
                          : 'border-gray-200 bg-white hover:border-primary/40 hover:shadow-md'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${opt.color} flex items-center justify-center mb-4 shadow-sm`}>
                        <FontAwesomeIcon icon={opt.icon} className="text-white text-xl" />
                      </div>
                      <span className="text-lg font-semibold text-gray-800 group-hover:text-primary transition-colors">
                        {opt.label}
                      </span>
                      {answers[quizQuestions[step].key] === opt.value && (
                        <div className="absolute top-3 right-3 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                        </div>
                      )}
                    </motion.button>
                  ))}
                </div>

                <button
                  onClick={() => setStep(s => s - 1)}
                  className="mt-6 text-gray-400 hover:text-gray-600 text-sm flex items-center gap-2 mx-auto transition-colors"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Indietro
                </button>
              </motion.div>
            ) : null}

            {/* Step 3: Contact form */}
            {step === 3 && (
              <motion.div
                key="contact"
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -60 }}
                transition={{ duration: 0.35 }}
                className="max-w-lg w-full"
              >
                <div className="text-center mb-8">
                  <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 text-sm font-medium px-4 py-2 rounded-full mb-4 border border-green-200">
                    <FontAwesomeIcon icon={faCheck} />
                    Ottimo! Abbiamo quasi finito
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    Dove ti contattiamo?
                  </h2>
                  <p className="text-gray-500 text-sm">
                    Un consulente ti richiamerà entro 24h con le offerte più adatte. Gratuito e senza obbligo.
                  </p>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-8 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                      <input
                        type="text"
                        value={contact.nome}
                        onChange={e => { setContact(c => ({ ...c, nome: e.target.value })); setErrors(er => ({ ...er, nome: '' })); }}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.nome ? 'border-red-400' : 'border-gray-200'} focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all`}
                        placeholder="Mario"
                      />
                      {errors.nome && <p className="text-red-500 text-xs mt-1">{errors.nome}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                      <input
                        type="text"
                        value={contact.cognome}
                        onChange={e => { setContact(c => ({ ...c, cognome: e.target.value })); setErrors(er => ({ ...er, cognome: '' })); }}
                        className={`w-full px-4 py-3 rounded-lg border ${errors.cognome ? 'border-red-400' : 'border-gray-200'} focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all`}
                        placeholder="Rossi"
                      />
                      {errors.cognome && <p className="text-red-500 text-xs mt-1">{errors.cognome}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefono *</label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={contact.telefono}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9\s\-+()]/g, '');
                        setContact(c => ({ ...c, telefono: val }));
                        setErrors(er => ({ ...er, telefono: '' }));
                      }}
                      onBlur={() => {
                        const tel = contact.telefono.replace(/[\s\-().+]/g, '');
                        if (tel && !/^\d{9,13}$/.test(tel)) {
                          setErrors(er => ({ ...er, telefono: 'Inserisci un numero valido (min. 9 cifre)' }));
                        }
                      }}
                      className={`w-full px-4 py-3 rounded-lg border ${errors.telefono ? 'border-red-400' : 'border-gray-200'} focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none transition-all`}
                      placeholder="333 1234567"
                      maxLength={16}
                    />
                    {errors.telefono && <p className="text-red-500 text-xs mt-1">{errors.telefono}</p>}
                  </div>

                  <PrivacyConsents
                    idPrefix="quiz"
                    values={{
                      privacy1: contact.privacy1,
                      privacy2: contact.privacy2,
                      privacy3: contact.privacy3,
                    }}
                    onChange={(consents) => {
                      setContact(c => ({ ...c, ...consents }));
                      if (consents.privacy1) setErrors(er => ({ ...er, privacy1: '' }));
                    }}
                    privacy1Error={errors.privacy1}
                  />

                  {formStatus === 'error' && (
                    <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
                      Errore nell'invio. Riprova o chiamaci al 02 4005 5045.
                    </div>
                  )}

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmit}
                    disabled={formStatus === 'loading'}
                    className="w-full bg-primary hover:bg-primary/90 disabled:bg-gray-300 text-white font-semibold py-4 rounded-xl shadow-lg shadow-primary/20 transition-colors flex items-center justify-center gap-2 text-lg"
                  >
                    {formStatus === 'loading' ? (
                      <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Invio in corso...</>
                    ) : (
                      <>Ricevi la tua consulenza gratuita</>
                    )}
                  </motion.button>
                </div>

                <button
                  onClick={() => setStep(2)}
                  className="mt-4 text-gray-400 hover:text-gray-600 text-sm flex items-center gap-2 mx-auto transition-colors"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                  Indietro
                </button>
              </motion.div>
            )}

            {/* Step 4: Thank You */}
            {step === 4 && (
              <motion.div
                key="thanks"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="max-w-lg w-full text-center"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FontAwesomeIcon icon={faCheck} className="text-green-600 text-3xl" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Grazie, {contact.nome}!
                </h2>
                <p className="text-lg text-gray-500 mb-8 leading-relaxed">
                  Un nostro consulente ti contatterà al numero <strong className="text-gray-700">{contact.telefono}</strong> entro 
                  24 ore lavorative per presentarti le offerte più vantaggiose per te.
                </p>
                <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
                  <p className="text-sm text-gray-400 mb-3">Riepilogo della tua richiesta</p>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-xs text-gray-400">Fornitura</p>
                      <p className="font-semibold text-gray-800 capitalize">{answers.tipo_fornitura.replace('_', ' e ')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Spesa</p>
                      <p className="font-semibold text-gray-800">
                        {answers.spesa_mensile === 'meno_50' ? '< 50€' :
                         answers.spesa_mensile === '50_100' ? '50-100€' :
                         answers.spesa_mensile === '100_150' ? '100-150€' : '> 150€'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Esigenza</p>
                      <p className="font-semibold text-gray-800 capitalize">{answers.situazione}</p>
                    </div>
                  </div>
                </div>
                <Link
                  to="/"
                  className="text-primary hover:text-secondary text-sm font-medium transition-colors"
                >
                  Torna alla homepage
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {/* Footer disclaimer */}
        <footer className="py-4 px-4 bg-white border-t border-gray-100">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[11px] text-gray-400">
              <FontAwesomeIcon icon={faInfoCircle} className="text-gray-300" />
              <p>
                Servizio indipendente di comparazione gestito da GT Energie Srl (P.IVA IT02812720809). 
                Non siamo il servizio clienti ufficiale di alcun fornitore. Consulenza gratuita, nessun obbligo.
              </p>
            </div>
            <div className="flex gap-4 text-[11px] text-gray-400 flex-shrink-0">
              <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy</Link>
              <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookie</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default QuizLanding;
